import * as ExcelJS from "exceljs";
import {
  addJustupBranding,
  setJustupMetadata,
} from "../../../../shared/utils/excel-branding.util";
import { OF } from "../../../of/models/of.model";
import { OfEtat } from "../../../of/enums/etat-of.enum";

const C = {
  headerBg: "FF1B2A4A",
  headerFont: "FFFFFFFF",
  rowAlt: "FFF8FAFC",
  border: "FFD0D5DD",
  warnBg: "FFFFF7E6",
  errorBg: "FFFEE7E6",
  okBg: "FFE6FFF5",
  age0_7: "FFE6FFF5",
  age8_14: "FFFFF7E6",
  age15_30: "FFFFE0B2",
  ageOver30: "FFFEE7E6",
  rawHeader: "FFE5E7EB",
  titleColor: "FF1B2A4A",
};

interface Stats {
  total: number;
  oldest: number | null;
  avg: number | null;
  topEtatLabel: string;
  topEtatCount: number;
}

export interface OfListExcelData {
  ofs: OF[];
  allOfs?: OF[];
  subTypeLabel: string;
  stats: Stats;
  attenteJours: (o: OF) => number | null;
  etatLabel: (e: OfEtat) => string;
}

export async function exportOfListExcel(data: OfListExcelData): Promise<void> {
  const wb = new ExcelJS.Workbook();
  setJustupMetadata(wb, { title: "OFs en attente" });

  await buildSyntheseSheet(wb, data);
  await buildOfsSheet(wb, data);
  await buildParLigneSheet(wb, data);
  await buildParAgeSheet(wb, data);
  await buildRawSheet(wb, data);

  const buf = await wb.xlsx.writeBuffer();
  const today = new Date().toISOString().slice(0, 10);
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `ofs-en-attente_${today}.xlsx`;
  a.click();
  URL.revokeObjectURL(a.href);
}

// ─────────────────────────────────────────────────────────────────────────────
// 1. Synthèse
// ─────────────────────────────────────────────────────────────────────────────
async function buildSyntheseSheet(
  wb: ExcelJS.Workbook,
  data: OfListExcelData,
): Promise<void> {
  const ws = wb.addWorksheet("Synthèse");
  ws.columns = [{ width: 36 }, { width: 22 }, { width: 12 }, { width: 12 }];

  setSheetTitle(ws, `OFs en attente — ${data.subTypeLabel}`, 4);
  await addJustupBranding(wb, ws, 4);

  let row = 3;
  ws.getCell(row, 1).value = "Indicateurs clés";
  ws.getCell(row, 1).font = { bold: true, size: 12, color: { argb: C.titleColor } };
  row++;
  const kpis: Array<[string, string | number]> = [
    ["Total OFs en attente", data.stats.total],
    ["Plus ancien (jours)", data.stats.oldest ?? "—"],
    [
      "Attente moyenne (jours)",
      data.stats.avg != null ? Math.round(data.stats.avg) : "—",
    ],
    ["État dominant", `${data.stats.topEtatLabel} (${data.stats.topEtatCount})`],
  ];
  for (const [label, value] of kpis) {
    ws.getCell(row, 1).value = label;
    ws.getCell(row, 1).font = { bold: true };
    ws.getCell(row, 2).value = value;
    row++;
  }

  // Répartition par état
  row += 1;
  ws.getCell(row, 1).value = "Répartition par état";
  ws.getCell(row, 1).font = { bold: true, size: 12, color: { argb: C.titleColor } };
  row++;
  styleHeader(ws.getRow(row), 3, ["État", "Nombre", "% total"]);
  row++;
  const byEtat = new Map<OfEtat, number>();
  for (const o of data.ofs) byEtat.set(o.etat, (byEtat.get(o.etat) ?? 0) + 1);
  for (const [etat, count] of byEtat) {
    ws.getCell(row, 1).value = data.etatLabel(etat);
    ws.getCell(row, 2).value = count;
    ws.getCell(row, 3).value =
      data.ofs.length > 0 ? count / data.ofs.length : 0;
    ws.getCell(row, 3).numFmt = "0.0%";
    row++;
  }

  // Répartition par âge d'attente
  row += 1;
  ws.getCell(row, 1).value = "Répartition par âge d'attente";
  ws.getCell(row, 1).font = { bold: true, size: 12, color: { argb: C.titleColor } };
  row++;
  styleHeader(ws.getRow(row), 3, ["Tranche", "Nombre", "% total"]);
  row++;
  for (const b of ageBuckets(data)) {
    ws.getCell(row, 1).value = b.label;
    ws.getCell(row, 1).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: b.color },
    };
    ws.getCell(row, 2).value = b.count;
    ws.getCell(row, 3).value =
      data.ofs.length > 0 ? b.count / data.ofs.length : 0;
    ws.getCell(row, 3).numFmt = "0.0%";
    row++;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// 2. OFs — détail filtré
// ─────────────────────────────────────────────────────────────────────────────
async function buildOfsSheet(
  wb: ExcelJS.Workbook,
  data: OfListExcelData,
): Promise<void> {
  const ws = wb.addWorksheet("OFs");
  ws.columns = [
    { key: "numero", width: 14 },
    { key: "ligne", width: 18 },
    { key: "article", width: 36 },
    { key: "code", width: 16 },
    { key: "quantite", width: 14 },
    { key: "qtyProd", width: 16 },
    { key: "etat", width: 22 },
    { key: "dateDebut", width: 14 },
    { key: "createDate", width: 14 },
    { key: "attente", width: 12 },
    { key: "bucket", width: 14 },
  ];
  const NCOLS = ws.columns.length;
  setSheetTitle(ws, `OFs en attente — ${data.subTypeLabel}`, NCOLS);
  await addJustupBranding(wb, ws, NCOLS);
  styleHeader(ws.getRow(2), NCOLS, [
    "Numéro",
    "Ligne",
    "Article",
    "Code article",
    "Qté prévue",
    "Qté produite",
    "État",
    "Date début",
    "Créé le",
    "Attente (j)",
    "Tranche",
  ]);

  for (const o of data.ofs) {
    const attente = data.attenteJours(o);
    const bucket = bucketLabel(attente);
    const r = ws.addRow({
      numero: o.numero ?? "",
      ligne: o.ligne?.denomination ?? "",
      article: o.articleInfos?.denomination ?? "",
      code: o.articleInfos?.code ?? "",
      quantite: o.quantite ?? 0,
      qtyProd: (o as any).quantiteProduite ?? "",
      etat: data.etatLabel(o.etat),
      dateDebut: o.dateDebut ? new Date(o.dateDebut as any) : null,
      createDate: (o as any).createDateTime
        ? new Date((o as any).createDateTime)
        : null,
      attente: attente != null ? attente : "",
      bucket,
    });
    r.getCell("dateDebut").numFmt = "dd/mm/yyyy";
    r.getCell("createDate").numFmt = "dd/mm/yyyy";
    r.getCell("quantite").numFmt = "#,##0.##";
    r.getCell("qtyProd").numFmt = "#,##0.##";
    if (attente != null) {
      const fill = colorForAttente(attente);
      if (fill) {
        r.getCell("attente").fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: fill },
        };
        r.getCell("bucket").fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: fill },
        };
      }
    }
  }

  ws.autoFilter = {
    from: { row: 2, column: 1 },
    to: { row: 2, column: NCOLS },
  };
  ws.views = [{ state: "frozen", ySplit: 2 }];
}

// ─────────────────────────────────────────────────────────────────────────────
// 3. Par ligne
// ─────────────────────────────────────────────────────────────────────────────
async function buildParLigneSheet(
  wb: ExcelJS.Workbook,
  data: OfListExcelData,
): Promise<void> {
  const ws = wb.addWorksheet("Par ligne");
  ws.columns = [
    { key: "ligne", width: 24 },
    { key: "count", width: 10 },
    { key: "qty", width: 16 },
    { key: "qtyProd", width: 16 },
    { key: "avg", width: 18 },
    { key: "max", width: 16 },
    { key: "min", width: 16 },
    { key: "pct", width: 12 },
  ];
  const NCOLS = ws.columns.length;
  setSheetTitle(ws, "OFs en attente — agrégation par ligne", NCOLS);
  await addJustupBranding(wb, ws, NCOLS);
  styleHeader(ws.getRow(2), NCOLS, [
    "Ligne",
    "Nb OFs",
    "Qté prévue totale",
    "Qté produite totale",
    "Attente moy. (j)",
    "Plus ancien (j)",
    "Plus récent (j)",
    "% du total",
  ]);

  type Agg = {
    ligne: string;
    count: number;
    qty: number;
    qtyProd: number;
    ages: number[];
  };
  const map = new Map<string, Agg>();
  for (const o of data.ofs) {
    const key = o.ligne?.denomination ?? "Sans ligne";
    const age = data.attenteJours(o);
    let agg = map.get(key);
    if (!agg) {
      agg = { ligne: key, count: 0, qty: 0, qtyProd: 0, ages: [] };
      map.set(key, agg);
    }
    agg.count++;
    agg.qty += Number(o.quantite ?? 0);
    agg.qtyProd += Number((o as any).quantiteProduite ?? 0);
    if (age != null) agg.ages.push(age);
  }
  const rows = [...map.values()].sort((a, b) => b.count - a.count);
  const total = data.ofs.length;
  const allAges: number[] = [];

  for (const a of rows) {
    allAges.push(...a.ages);
    const r = ws.addRow({
      ligne: a.ligne,
      count: a.count,
      qty: a.qty,
      qtyProd: a.qtyProd,
      avg:
        a.ages.length > 0
          ? Math.round(a.ages.reduce((s, v) => s + v, 0) / a.ages.length)
          : "",
      max: a.ages.length > 0 ? Math.max(...a.ages) : "",
      min: a.ages.length > 0 ? Math.min(...a.ages) : "",
      pct: total > 0 ? a.count / total : 0,
    });
    r.getCell("qty").numFmt = "#,##0.##";
    r.getCell("qtyProd").numFmt = "#,##0.##";
    r.getCell("pct").numFmt = "0.0%";
  }

  // Ligne TOTAL
  const totalRow = ws.addRow({
    ligne: "TOTAL",
    count: total,
    qty: rows.reduce((s, a) => s + a.qty, 0),
    qtyProd: rows.reduce((s, a) => s + a.qtyProd, 0),
    avg:
      allAges.length > 0
        ? Math.round(allAges.reduce((s, v) => s + v, 0) / allAges.length)
        : "",
    max: allAges.length > 0 ? Math.max(...allAges) : "",
    min: allAges.length > 0 ? Math.min(...allAges) : "",
    pct: 1,
  });
  totalRow.eachCell((c) => {
    c.font = { bold: true };
    c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE5E7EB" } };
  });
  totalRow.getCell("pct").numFmt = "0.0%";
  totalRow.getCell("qty").numFmt = "#,##0.##";
  totalRow.getCell("qtyProd").numFmt = "#,##0.##";

  ws.autoFilter = {
    from: { row: 2, column: 1 },
    to: { row: 2, column: NCOLS },
  };
  ws.views = [{ state: "frozen", ySplit: 2 }];
}

// ─────────────────────────────────────────────────────────────────────────────
// 4. Par âge × état
// ─────────────────────────────────────────────────────────────────────────────
async function buildParAgeSheet(
  wb: ExcelJS.Workbook,
  data: OfListExcelData,
): Promise<void> {
  const ws = wb.addWorksheet("Par âge");
  ws.columns = [
    { key: "bucket", width: 18 },
    { key: "etat", width: 22 },
    { key: "count", width: 10 },
    { key: "qty", width: 16 },
    { key: "pct", width: 12 },
  ];
  const NCOLS = ws.columns.length;
  setSheetTitle(ws, "OFs en attente — pivot âge × état", NCOLS);
  await addJustupBranding(wb, ws, NCOLS);
  styleHeader(ws.getRow(2), NCOLS, [
    "Tranche",
    "État",
    "Nb OFs",
    "Quantité totale",
    "% du total",
  ]);

  type Cell = { count: number; qty: number };
  const grid = new Map<string, Map<string, Cell>>();
  for (const o of data.ofs) {
    const age = data.attenteJours(o);
    const bucket = bucketLabel(age);
    const etat = data.etatLabel(o.etat);
    let row = grid.get(bucket);
    if (!row) {
      row = new Map();
      grid.set(bucket, row);
    }
    let cell = row.get(etat);
    if (!cell) {
      cell = { count: 0, qty: 0 };
      row.set(etat, cell);
    }
    cell.count++;
    cell.qty += Number(o.quantite ?? 0);
  }

  const total = data.ofs.length;
  const orderedBuckets = ["0–7 j", "8–14 j", "15–30 j", "> 30 j", "Inconnu"];
  for (const bk of orderedBuckets) {
    const subMap = grid.get(bk);
    if (!subMap || subMap.size === 0) continue;
    for (const [etat, cell] of subMap) {
      const r = ws.addRow({
        bucket: bk,
        etat,
        count: cell.count,
        qty: cell.qty,
        pct: total > 0 ? cell.count / total : 0,
      });
      const fill = bucketColor(bk);
      if (fill) {
        r.getCell("bucket").fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: fill },
        };
      }
      r.getCell("qty").numFmt = "#,##0.##";
      r.getCell("pct").numFmt = "0.0%";
    }
  }

  ws.autoFilter = {
    from: { row: 2, column: 1 },
    to: { row: 2, column: NCOLS },
  };
  ws.views = [{ state: "frozen", ySplit: 2 }];
}

// ─────────────────────────────────────────────────────────────────────────────
// 5. Données brutes — toutes OFs sans filtre
// ─────────────────────────────────────────────────────────────────────────────
async function buildRawSheet(
  wb: ExcelJS.Workbook,
  data: OfListExcelData,
): Promise<void> {
  const source = data.allOfs ?? data.ofs ?? [];
  const ws = wb.addWorksheet("Données brutes");
  ws.columns = [
    { key: "id", width: 38 },
    { key: "numero", width: 14 },
    { key: "etat", width: 22 },
    { key: "quantite", width: 12 },
    { key: "quantiteProduite", width: 16 },
    { key: "dateDebut", width: 14 },
    { key: "dateFin", width: 14 },
    { key: "createDateTime", width: 18 },
    { key: "ligneId", width: 38 },
    { key: "ligneDenom", width: 22 },
    { key: "articleId", width: 38 },
    { key: "artDenom", width: 32 },
    { key: "artCode", width: 16 },
    { key: "scoreTotal", width: 12 },
    { key: "scoreMatiere", width: 12 },
    { key: "scorePersonnel", width: 14 },
    { key: "valideProd", width: 12 },
    { key: "valideQualite", width: 14 },
    { key: "specId", width: 38 },
    { key: "attente", width: 12 },
    { key: "isActive", width: 10 },
  ];
  const NCOLS = ws.columns.length;
  setSheetTitle(ws, "Données brutes — toutes les OFs", NCOLS);
  await addJustupBranding(wb, ws, NCOLS);

  const headerRow = ws.getRow(2);
  headerRow.values = [
    "OF.id",
    "OF.numero",
    "OF.etat",
    "OF.quantite",
    "OF.quantiteProduite",
    "OF.dateDebut",
    "OF.dateFin",
    "OF.createDateTime",
    "ligne.id",
    "ligne.denomination",
    "article.id",
    "articleInfos.denomination",
    "articleInfos.code",
    "scoreTotal",
    "scoreMatiere",
    "scorePersonnel",
    "valideProd",
    "valideQualite",
    "specificationId",
    "Attente (j)",
    "isActive",
  ];
  headerRow.eachCell((c) => {
    c.font = { bold: true, size: 11 };
    c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.rawHeader } };
    c.border = { bottom: { style: "thin", color: { argb: C.border } } };
  });

  for (const o of source) {
    const r = ws.addRow({
      id: o.id ?? "",
      numero: o.numero ?? "",
      etat: o.etat ?? "",
      quantite: o.quantite ?? "",
      quantiteProduite: (o as any).quantiteProduite ?? "",
      dateDebut: o.dateDebut ? new Date(o.dateDebut as any) : null,
      dateFin: (o as any).dateFin ? new Date((o as any).dateFin) : null,
      createDateTime: (o as any).createDateTime
        ? new Date((o as any).createDateTime)
        : null,
      ligneId: (o as any).ligneId ?? o.ligne?.id ?? "",
      ligneDenom: o.ligne?.denomination ?? "",
      articleId: (o as any).articleId ?? "",
      artDenom: o.articleInfos?.denomination ?? "",
      artCode: o.articleInfos?.code ?? "",
      scoreTotal: (o as any).scoreTotal ?? "",
      scoreMatiere: (o as any).scoreMatiere ?? "",
      scorePersonnel: (o as any).scorePersonnel ?? "",
      valideProd: (o as any).valideProd ?? "",
      valideQualite: (o as any).valideQualite ?? "",
      specId: (o as any).specificationId ?? "",
      attente: data.attenteJours(o) ?? "",
      isActive: (o as any).isActive ?? "",
    });
    r.getCell("dateDebut").numFmt = "dd/mm/yyyy";
    r.getCell("dateFin").numFmt = "dd/mm/yyyy";
    r.getCell("createDateTime").numFmt = "dd/mm/yyyy hh:mm";
  }

  ws.autoFilter = {
    from: { row: 2, column: 1 },
    to: { row: 2, column: NCOLS },
  };
  ws.views = [{ state: "frozen", ySplit: 2 }];
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
function setSheetTitle(
  ws: ExcelJS.Worksheet,
  title: string,
  ncols: number,
): void {
  const row = ws.getRow(1);
  row.height = 22;
  ws.mergeCells(1, 1, 1, Math.max(1, ncols - 1));
  row.getCell(1).value = title;
  row.getCell(1).font = { bold: true, size: 14, color: { argb: C.titleColor } };
  row.getCell(1).alignment = { vertical: "middle" };
}

function styleHeader(
  row: ExcelJS.Row,
  ncols: number,
  values?: string[],
): void {
  if (values) {
    // .values = écrase index 0 → on positionne via getCell pour préserver les
    // colonnes définies (clé ↔ index).
    for (let i = 0; i < values.length; i++) {
      row.getCell(i + 1).value = values[i];
    }
  }
  row.height = 20;
  for (let i = 1; i <= ncols; i++) {
    const c = row.getCell(i);
    c.font = { bold: true, color: { argb: C.headerFont } };
    c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.headerBg } };
    c.alignment = { vertical: "middle" };
    c.border = { bottom: { style: "thin", color: { argb: C.border } } };
  }
}

function ageBuckets(
  data: OfListExcelData,
): Array<{ label: string; count: number; color: string }> {
  const buckets = [
    { label: "0–7 j", count: 0, color: C.age0_7 },
    { label: "8–14 j", count: 0, color: C.age8_14 },
    { label: "15–30 j", count: 0, color: C.age15_30 },
    { label: "> 30 j", count: 0, color: C.ageOver30 },
    { label: "Inconnu", count: 0, color: "FFE5E7EB" },
  ];
  for (const o of data.ofs) {
    const age = data.attenteJours(o);
    if (age == null) buckets[4].count++;
    else if (age <= 7) buckets[0].count++;
    else if (age <= 14) buckets[1].count++;
    else if (age <= 30) buckets[2].count++;
    else buckets[3].count++;
  }
  return buckets;
}

function bucketLabel(age: number | null): string {
  if (age == null) return "Inconnu";
  if (age <= 7) return "0–7 j";
  if (age <= 14) return "8–14 j";
  if (age <= 30) return "15–30 j";
  return "> 30 j";
}

function bucketColor(label: string): string | null {
  switch (label) {
    case "0–7 j":
      return C.age0_7;
    case "8–14 j":
      return C.age8_14;
    case "15–30 j":
      return C.age15_30;
    case "> 30 j":
      return C.ageOver30;
    default:
      return null;
  }
}

function colorForAttente(age: number): string | null {
  if (age <= 7) return null;
  if (age <= 14) return C.warnBg;
  if (age <= 30) return "FFFFE0B2";
  return C.errorBg;
}
